import { useTranslation } from 'react-i18next';
import { validate, ValidationRule } from './validate';
import { Namespace } from '../../../translation/namespace';
import { isNonEmptyArray, NonEmptyArray } from '../../../lib/typeGuards';
import { readFileAsImageString, toHtmlImageElement } from './file.utils';

export type ImageMetaData = {
  src: string;
  width: number;
  height: number;
  fileName: string;
};

export type ValidSelectedImage = {
  file: File;
  errors: [];
  image: ImageMetaData;
};
function ValidSelectedImage(file: File, image: ImageMetaData): ValidSelectedImage {
  return { file, image, errors: [] };
}
export type InvalidSelectedImage = {
  file: File;
  errors: NonEmptyArray<string>;
};
function InvalidSelectedImage(file: File, errors: NonEmptyArray<string>): InvalidSelectedImage {
  return { file, errors };
}
export type SelectedImage = ValidSelectedImage | InvalidSelectedImage;

export function isValid(selectedImage?: SelectedImage): selectedImage is ValidSelectedImage {
  return selectedImage ? selectedImage.errors.length === 0 : false;
}

export function isInvalid(selectedImage?: SelectedImage): selectedImage is InvalidSelectedImage {
  return selectedImage ? selectedImage.errors.length > 0 : false;
}

export function useValidateImage() {
  const { t } = useTranslation(Namespace.FileUpload);
  return (file: File, validationRules: ValidationRule[]): Promise<SelectedImage> => {
    const fileErrors = validate(file, validationRules);

    if (isNonEmptyArray(fileErrors)) {
      // return early if file does not satisfy constraints
      return Promise.resolve(InvalidSelectedImage(file, fileErrors));
    }

    return readFileAsImageString(file)
      .then(toHtmlImageElement)
      .then(image => ({
        fileName: file.name,
        width: image.width,
        height: image.height,
        src: image.src,
      }))
      .then((image) => {
        const errors = fileErrors.concat(validate(image, validationRules));
        return isNonEmptyArray(errors) ? InvalidSelectedImage(file, errors) : ValidSelectedImage(file, image);
      })
      .catch(() =>
        InvalidSelectedImage(
          file,
          [t('validationErrorMessages.unkownErrorWhileLoadingImage', { fileName: file.name })],
        ),
      );
  };
}
